/**
 * 前端显示逻辑引擎（Step 2 的 backend/src/lib/display-logic.mjs 的 ts 桥接）
 * 与后端逻辑保持一致；预览页和填卷页都用这个算"当前应显示哪些题"
 */

import type { SurveyConfig, DisplayLogic, Condition } from '../components/editor/types';

interface Ctx {
  answers: Record<string, unknown>;
  visibleIds: Set<string>;
}

/** 文本题答案配图：{ text, images } 对象取 text 参与条件求值 */
function answerText(value: unknown): unknown {
  if (value && typeof value === 'object' && !Array.isArray(value) && 'text' in value) {
    return (value as { text?: unknown }).text;
  }
  return value;
}

const OPS: Record<string, (c: Condition, a: Record<string, unknown>) => boolean> = {
  equals(c, a) {
    const v = answerText(a[c.question]);
    if (v === undefined || v === null) return false;
    return v === c.value || String(v) === String(c.value);
  },
  contains(c, a) {
    const v = answerText(a[c.question]);
    if (v === undefined || v === null) return false;
    if (Array.isArray(v)) {
      return v.includes(c.value) || v.some((x) => String(x) === String(c.value));
    }
    if (typeof v === 'string') return v.includes(String(c.value));
    return false;
  },
  gt(c, a) {
    const v = a[c.question];
    if (typeof v !== 'number' || typeof c.value !== 'number') return false;
    return v > c.value;
  },
  lt(c, a) {
    const v = a[c.question];
    if (typeof v !== 'number' || typeof c.value !== 'number') return false;
    return v < c.value;
  },
  between(c, a) {
    const v = a[c.question];
    if (typeof v !== 'number') return false;
    if (!Array.isArray(c.value) || c.value.length !== 2) return false;
    const [mn, mx] = c.value as [number, number];
    if (typeof mn !== 'number' || typeof mx !== 'number') return false;
    return v >= mn && v <= mx;
  },
};

export function evaluateCondition(c: Condition, ctx: Ctx): boolean {
  if (!c || typeof c.question !== 'string') return false;
  if (ctx.visibleIds && !ctx.visibleIds.has(c.question)) return false;
  const op = OPS[c.op];
  if (!op) return false;
  return op(c, ctx.answers);
}

export function evaluateDisplayLogic(logic: DisplayLogic | undefined, ctx: Ctx): boolean {
  if (!logic || !Array.isArray(logic.conditions) || logic.conditions.length === 0) return true;
  const results = logic.conditions.map((c) => evaluateCondition(c, ctx));
  if (logic.operator === 'OR') return results.some(Boolean);
  return results.every(Boolean);
}

export function computeVisibleQuestions(survey: SurveyConfig, answers: Record<string, unknown> = {}): string[] {
  if (!survey?.questions) return [];
  const visibleIds = new Set<string>();
  const ctx: Ctx = { answers, visibleIds };
  for (const q of survey.questions) {
    if (typeof q.id !== 'string') continue;
    if (evaluateDisplayLogic(q.displayLogic, ctx)) visibleIds.add(q.id);
  }
  return survey.questions.filter((q) => visibleIds.has(q.id)).map((q) => q.id);
}
